import React, { useMemo, useState } from 'react';
import { Loader2, AlertCircle, Grid3x3, Info, Building2, Package } from 'lucide-react';
import { useMitreMatrix, useMitreVendors, useMitreCoverage } from './hooks/useMitreData';
import './MitreMatrix.css';

// Score 0..3 → CSS color class. null/undefined treated as 0 (no coverage).
function coverageClass(coverage) {
  if (!coverage) return 'mm-cell--none';
  const best = Math.max(coverage.protect_score || 0, coverage.detect_score || 0, coverage.respond_score || 0);
  return ['mm-cell--none', 'mm-cell--minimal', 'mm-cell--partial', 'mm-cell--significant'][best];
}

// Human label for a 0..3 score
function scoreLabel(s) {
  return ['none', 'minimal', 'partial', 'significant'][s || 0];
}

const MitreMatrix = ({ selectedDepartment }) => {
  const { data: matrix, isLoading: matrixLoading, error: matrixError } = useMitreMatrix();
  const { data: vendorsResp } = useMitreVendors();
  const [mode, setMode] = useState('vendor'); // 'vendor' | 'department'
  const [selectedVendor, setSelectedVendor] = useState('');

  // Active inputs depend on mode. Each mode disables the other so we never
  // fire two coverage requests at once.
  const departmentId = mode === 'department' ? selectedDepartment?.department_id : null;
  const activeVendor = mode === 'vendor' ? selectedVendor : '';
  const { data: coverageResp, isFetching: coverageLoading } = useMitreCoverage({
    vendor: activeVendor,
    departmentId,
  });

  // Index coverage by technique_id for O(1) cell lookup
  const coverageByTechnique = useMemo(() => {
    const map = new Map();
    for (const row of coverageResp?.coverage || []) {
      map.set(row.technique_id, row);
    }
    return map;
  }, [coverageResp]);

  // Group top-level techniques (no subtechniques in V1) under each tactic
  const techniquesByTactic = useMemo(() => {
    if (!matrix) return new Map();
    const map = new Map();
    for (const t of matrix.tactics) map.set(t.tactic_id, []);
    for (const tech of matrix.techniques) {
      if (tech.is_subtechnique) continue;
      for (const tacticId of tech.tactic_ids || []) {
        if (map.has(tacticId)) map.get(tacticId).push(tech);
      }
    }
    return map;
  }, [matrix]);

  // Headline coverage stats for the legend strip
  const coverageStats = useMemo(() => {
    if (!coverageResp) return null;
    const counts = { none: 0, minimal: 0, partial: 0, significant: 0 };
    const totalTechniques = (matrix?.techniques || []).filter(t => !t.is_subtechnique).length;
    for (const tech of matrix?.techniques || []) {
      if (tech.is_subtechnique) continue;
      const c = coverageByTechnique.get(tech.technique_id);
      const best = c
        ? Math.max(c.protect_score || 0, c.detect_score || 0, c.respond_score || 0)
        : 0;
      counts[scoreLabel(best)]++;
    }
    return { counts, totalTechniques, covered: totalTechniques - counts.none };
  }, [coverageResp, coverageByTechnique, matrix]);

  if (matrixLoading) {
    return (
      <div className="mm-loading">
        <Loader2 size={28} className="spin" />
        <p>Loading MITRE ATT&CK framework…</p>
      </div>
    );
  }

  if (matrixError) {
    return (
      <div className="mm-error">
        <AlertCircle size={28} />
        <p>Failed to load MITRE matrix: {matrixError.message}</p>
      </div>
    );
  }

  return (
    <div className="mm-container">
      {/* Toolbar: vendor selector + coverage stats */}
      <div className="mm-toolbar">
        <div className="mm-toolbar-left">
          <Grid3x3 size={18} style={{ color: 'var(--color-primary)' }} />
          <span className="mm-title">MITRE ATT&CK Enterprise — v{matrix?.mitre_version}</span>
          <span className="mm-counts">
            {matrix?.counts.tactics} tactics · {matrix?.counts.techniques} techniques
          </span>
        </div>

        <div className="mm-toolbar-right">
          {/* Mode toggle: Vendor | Department */}
          <div className="mm-mode-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'vendor'}
              className={`mm-mode-btn ${mode === 'vendor' ? 'active' : ''}`}
              onClick={() => setMode('vendor')}
              title="Show coverage for a single vendor"
            >
              <Package size={13} />
              Vendor
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'department'}
              className={`mm-mode-btn ${mode === 'department' ? 'active' : ''}`}
              onClick={() => setMode('department')}
              title="Show union coverage of every vendor this department owns"
            >
              <Building2 size={13} />
              Department
            </button>
          </div>

          {mode === 'vendor' ? (
            <select
              id="mm-vendor-select"
              className="mm-vendor-select"
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              aria-label="Vendor"
            >
              <option value="">— Select a vendor —</option>
              {(vendorsResp?.vendors || []).map(v => (
                <option key={v.vendor_name} value={v.vendor_name}>
                  {v.vendor_name} ({v.technique_count} techniques)
                </option>
              ))}
            </select>
          ) : (
            <div className="mm-dept-display">
              {selectedDepartment ? (
                <>
                  <Building2 size={13} />
                  <span>{selectedDepartment.acronym || selectedDepartment.official_name}</span>
                </>
              ) : (
                <span className="mm-dept-empty">Pick a department in the page header</span>
              )}
            </div>
          )}

          {coverageLoading && <Loader2 size={14} className="spin" />}
        </div>
      </div>

      {/* Coverage stats strip — only shown when a vendor is selected */}
      {coverageStats && (
        <div className="mm-stats">
          <div className="mm-stats-headline">
            <strong>{coverageStats.covered}</strong>
            <span> of </span>
            <strong>{coverageStats.totalTechniques}</strong>
            <span> techniques covered ({Math.round((coverageStats.covered / coverageStats.totalTechniques) * 100)}%)</span>
          </div>
          <div className="mm-stats-legend">
            <span className="mm-legend-item"><span className="mm-swatch mm-cell--significant" /> significant ({coverageStats.counts.significant})</span>
            <span className="mm-legend-item"><span className="mm-swatch mm-cell--partial" /> partial ({coverageStats.counts.partial})</span>
            <span className="mm-legend-item"><span className="mm-swatch mm-cell--minimal" /> minimal ({coverageStats.counts.minimal})</span>
            <span className="mm-legend-item"><span className="mm-swatch mm-cell--none" /> not covered ({coverageStats.counts.none})</span>
          </div>
        </div>
      )}

      {/* Path A disclaimer — surfaced whenever coverage is shown */}
      {coverageResp && (
        <div className="mm-disclaimer">
          <Info size={13} />
          {mode === 'vendor' ? (
            <span>
              Coverage shows what <strong>{selectedVendor}'s product family</strong> can potentially address — assumes the
              customer has deployed and configured the relevant security services. This is capability mapping, not deployed efficacy.
            </span>
          ) : (
            <span>
              Coverage is the union of MITRE-mapped products this department has procurement spend on
              {coverageResp.owned_vendors?.length > 0 && (
                <> — currently <strong>{coverageResp.owned_vendors.join(', ')}</strong></>
              )}
              . It assumes the relevant security services are deployed and configured. Capability mapping, not deployed efficacy.
            </span>
          )}
        </div>
      )}

      {/* Empty state — dept mode with no MITRE-mapped vendors */}
      {mode === 'department' && coverageResp && coverageResp.owned_vendors?.length === 0 && (
        <div className="mm-empty-state">
          <Info size={16} />
          <div>
            <strong>{coverageResp.department_acronym || coverageResp.department_name}</strong> has no procurement spend
            on any MITRE-mapped vendor yet. As you add tech_components for this department's purchases, coverage will
            populate here.
          </div>
        </div>
      )}

      {/* Matrix grid */}
      <div className="mm-grid-wrap">
        <div
          className="mm-grid"
          style={{ gridTemplateColumns: `repeat(${matrix?.tactics.length || 14}, minmax(170px, 1fr))` }}
        >
          {/* Tactic header row */}
          {matrix?.tactics.map(tactic => (
            <div key={`h-${tactic.tactic_id}`} className="mm-tactic-header">
              <div className="mm-tactic-name">{tactic.name}</div>
              <div className="mm-tactic-count">
                {techniquesByTactic.get(tactic.tactic_id)?.length || 0}
              </div>
            </div>
          ))}

          {/* Technique cell columns */}
          {matrix?.tactics.map(tactic => {
            const techniques = techniquesByTactic.get(tactic.tactic_id) || [];
            return (
              <div key={`c-${tactic.tactic_id}`} className="mm-tactic-column">
                {techniques.map(tech => {
                  const coverage = coverageByTechnique.get(tech.technique_id);
                  const cls = coverageClass(coverage);
                  // Tooltip differs between modes:
                  //   vendor mode:     show capabilities (specific products)
                  //   department mode: show contributing_vendors (which
                  //                    of the dept's owned vendors hit it)
                  let tooltip;
                  if (coverage) {
                    const scoreLine = `Protect: ${scoreLabel(coverage.protect_score)} · ` +
                                      `Detect: ${scoreLabel(coverage.detect_score)} · ` +
                                      `Respond: ${scoreLabel(coverage.respond_score)}`;
                    const detailKey = mode === 'department' ? 'contributing_vendors' : 'capabilities';
                    const detailLabel = mode === 'department' ? 'Owned via' : 'Capabilities';
                    const detailItems = coverage[detailKey] || [];
                    const detailLine = detailItems.length > 0
                      ? `${detailLabel}: ${detailItems.slice(0, 8).join(', ')}` +
                        (detailItems.length > 8 ? `, +${detailItems.length - 8} more` : '')
                      : '';
                    tooltip = `${tech.technique_id} — ${tech.name}\n${scoreLine}` +
                              (detailLine ? `\n${detailLine}` : '');
                  } else {
                    const noCovLabel = mode === 'vendor'
                      ? (selectedVendor ? `\n(no coverage data for ${selectedVendor})` : '')
                      : (departmentId ? `\n(no MITRE-mapped vendor in this dept covers it)` : '');
                    tooltip = `${tech.technique_id} — ${tech.name}${noCovLabel}`;
                  }
                  return (
                    <div
                      key={`${tactic.tactic_id}-${tech.technique_id}`}
                      className={`mm-cell ${cls}`}
                      title={tooltip}
                    >
                      <div className="mm-cell-id">{tech.technique_id}</div>
                      <div className="mm-cell-name">{tech.name}</div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MitreMatrix;
